import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, organizationsAPI } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('cset_token');
    const savedUser = localStorage.getItem('cset_user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
        // Fetch fresh user data with organization info
        await refreshUserData();
      } catch (error) {
        console.error('Auth check failed:', error);
        logout();
      }
    }
    setLoading(false);
  };

  const refreshUserData = async () => {
    try {
      const response = await authAPI.getMe();
      const data = response.data;
      
      // Update organization state
      setCurrentOrganization(data.currentOrganization);
      setOrganizations(data.organizations || []);
      
      // Update user data
      const userData = {
        userId: data.userId,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        isAdmin: data.isAdmin,
        isSuperAdmin: data.isSuperAdmin,
        role: data.isAdmin ? 'admin' : 'user',
        currentOrganization: data.currentOrganization,
        organizations: data.organizations
      };
      
      localStorage.setItem('cset_user', JSON.stringify(userData));
      setUser(userData);
      
      return data;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const switchOrganization = async (orgId) => {
    try {
      const response = await organizationsAPI.switchOrg(orgId);
      const { organization } = response.data;
      
      setCurrentOrganization(organization);
      
      // Update organizations list to reflect current
      setOrganizations(prev => prev.map(org => ({
        ...org,
        isCurrent: org.id === orgId
      })));
      
      // Update user data
      setUser(prev => ({
        ...prev,
        currentOrganization: organization
      }));
      
      return organization;
    } catch (error) {
      console.error('Failed to switch organization:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, userId, email: userEmail, firstName, lastName, isAdmin, isSuperAdmin, role } = response.data;
    
    // Determine role - support legacy isAdmin field
    const userRole = role || (isAdmin ? 'admin' : 'user');
    
    const userData = { 
      userId, 
      email: userEmail, 
      firstName, 
      lastName, 
      isAdmin: isAdmin || false,
      isSuperAdmin: isSuperAdmin || false,
      role: userRole
    };
    
    localStorage.setItem('cset_token', token);
    localStorage.setItem('cset_user', JSON.stringify(userData));
    
    setUser(userData);
    setIsAuthenticated(true);
    
    // Fetch organization data after login
    await refreshUserData();
    
    return userData;
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await authAPI.register({ email, password, firstName, lastName });
    const { token, userId, email: userEmail, isAdmin, role } = response.data;
    
    const userRole = role || 'user';
    
    const userData = { 
      userId, 
      email: userEmail, 
      firstName, 
      lastName, 
      isAdmin: isAdmin || false,
      role: userRole
    };
    
    localStorage.setItem('cset_token', token);
    localStorage.setItem('cset_user', JSON.stringify(userData));
    
    setUser(userData);
    setIsAuthenticated(true);
    
    // Fetch organization data after registration
    await refreshUserData();
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('cset_token');
    localStorage.removeItem('cset_user');
    setUser(null);
    setIsAuthenticated(false);
    setCurrentOrganization(null);
    setOrganizations([]);
  };

  // Helper to check if user is a client
  const isClient = () => user?.role === 'client';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      currentOrganization,
      organizations,
      login,
      register,
      logout,
      checkAuth,
      isClient,
      switchOrganization,
      refreshUserData
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
